import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RootState from '../redux/states/root.state';
import { AppDispatch } from '../redux/store';
import { resetLiveData, setLiveData } from '../redux/reducers/live.reducer';
import { resetSettingsData, setSettingsData } from '../redux/reducers/settings.reducer';
import { resetConstData, setConstData } from '../redux/reducers/const.reducer';
import { resetErrorData } from '../redux/reducers/error.reducer';
import { resetUpdateStatusData } from '../redux/reducers/updateStatus.reducer';
import { setStatusData } from '../redux/reducers/status.reducer';
import { fetchV1Snapshot } from '../services/api/v1Mqtt.api';
import { snapshotToSlices } from '../services/mqtt/v1/v1Snapshot';
import { V1Error, v1ErrorsFromPayload } from '../services/mqtt/v1/v1Errors';

/**
 * The details page's counterpart of `useV1Mqtt`: fills the same redux slices for a V1 /
 * Coldfire spa from the legacy database, once on load and again on each `refresh()`.
 * Pass an empty `spaId` to keep it idle.
 *
 * A failed refresh keeps the last good data on screen; only `failed` changes.
 */
const useV1Snapshot = (spaId: string) => {
    const dispatch: AppDispatch = useDispatch();
    const accessToken = useSelector((state: RootState) => state.user.accessToken);
    const [connected, setConnected] = useState(false);
    const [lastReported, setLastReported] = useState<string | null>(null);
    const [errors, setErrors] = useState<V1Error[]>([]);
    const [failed, setFailed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    // Drops a slow response that lands after the page moved to another spa.
    const currentSpaRef = useRef(spaId);

    const refresh = useCallback(async () => {
        if (!spaId) return;
        setRefreshing(true);
        try {
            const snapshot = await fetchV1Snapshot(accessToken, spaId);
            if (currentSpaRef.current !== spaId) return;
            const slices = snapshotToSlices(snapshot);
            dispatch(setLiveData(slices.live));
            dispatch(setSettingsData(slices.settings));
            dispatch(setConstData(slices.const));
            setErrors(v1ErrorsFromPayload(snapshot.channels.errors) ?? []);
            setConnected(snapshot.connected);
            setLastReported(snapshot.last_reported);
            setFailed(false);
        } catch {
            if (currentSpaRef.current === spaId) setFailed(true);
        } finally {
            setRefreshing(false);
        }
    }, [accessToken, dispatch, spaId]);

    useEffect(() => {
        currentSpaRef.current = spaId;
        if (!spaId) return;

        // A fresh spa: nothing from the previous one may linger on the page.
        dispatch(resetLiveData());
        dispatch(resetSettingsData());
        dispatch(resetConstData());
        dispatch(resetErrorData());
        dispatch(resetUpdateStatusData());
        // V1 has no STAT61; null is what makes the details grid leave NA/Euro blank.
        dispatch(setStatusData({ STAT61: null }));
        setConnected(false);
        setLastReported(null);
        setErrors([]);
        setFailed(false);

        setLoading(true);
        refresh().finally(() => setLoading(false));
    }, [dispatch, refresh, spaId]);

    return { connected, lastReported, errors, failed, loading, refreshing, refresh };
};

export default useV1Snapshot;
